/**
 * settings/storage.ts — load / save user preferences via a
 * KeyValueStorage backend (default: browser localStorage).
 *
 * Storage layout: a single JSON-encoded `Settings` blob at
 * `su:settings`. No slot model — there's only one Settings record per
 * install (multi-profile support would land much later if at all).
 *
 * Reuses the SaveStorageBackend type from src/save/storage.ts since the
 * three-method shape is identical to the slice of the browser Storage
 * API both modules need.
 *
 * On load, missing fields fall back to DEFAULT_SETTINGS via a shallow
 * merge per sub-object — this keeps old saved Settings forward-compatible
 * if a v2M4 user upgrades to a v2M5 build that adds a new gameplay
 * field (the new field takes its default rather than crashing on an
 * undefined read).
 */

import {
  createInMemoryBackend,
  type SaveStorageBackend,
} from '../save/storage';
import {
  DEFAULT_ACCESSIBILITY_SETTINGS,
  DEFAULT_AUDIO_SETTINGS,
  DEFAULT_CONTROLS_SETTINGS,
  DEFAULT_GAMEPLAY_SETTINGS,
  DEFAULT_GRAPHICS_SETTINGS,
  DEFAULT_INTERFACE_SETTINGS,
  SETTINGS_SCHEMA_VERSION,
  type Settings,
} from './types';

const STORAGE_KEY = 'su:settings';

export interface SettingsStorage {
  /**
   * Load the persisted Settings. Returns DEFAULT_SETTINGS if nothing is
   * stored, the blob is unparseable, or the schemaVersion mismatches —
   * all of which are recoverable by writing fresh.
   */
  load(): Settings;
  /** Persist the given Settings, replacing any existing blob. */
  save(settings: Settings): void;
}

/**
 * Re-export so settings tests don't need to reach into save/storage.
 */
export { createInMemoryBackend };

export interface SettingsStorageOpts {
  backend?: SaveStorageBackend;
}

function defaultBackend(): SaveStorageBackend {
  const ls = (globalThis as unknown as { localStorage?: SaveStorageBackend })
    .localStorage;
  if (!ls) {
    throw new Error(
      'No localStorage available — pass a SaveStorageBackend explicitly.',
    );
  }
  return ls;
}

/**
 * Shallow-merge a partial Settings into the defaults so an upgrade that
 * adds a gameplay field doesn't crash on the missing key. Does NOT walk
 * deeper — sub-objects merge at the top level only. Future shape changes
 * within a sub-object (e.g. adding a field to GameplaySettings) need an
 * explicit cascade if they're forward-compat-critical; here we treat
 * the sub-object as atomic and merge per sub-object key.
 */
function mergeWithDefaults(partial: Partial<Settings>): Settings {
  return {
    schemaVersion: SETTINGS_SCHEMA_VERSION,
    gameplay: {
      ...DEFAULT_GAMEPLAY_SETTINGS,
      ...(partial.gameplay ?? {}),
    },
    audio: {
      ...DEFAULT_AUDIO_SETTINGS,
      ...(partial.audio ?? {}),
    },
    graphics: {
      ...DEFAULT_GRAPHICS_SETTINGS,
      ...(partial.graphics ?? {}),
    },
    controls: {
      ...DEFAULT_CONTROLS_SETTINGS,
      ...(partial.controls ?? {}),
    },
    accessibility: {
      ...DEFAULT_ACCESSIBILITY_SETTINGS,
      ...(partial.accessibility ?? {}),
    },
    interface: {
      ...DEFAULT_INTERFACE_SETTINGS,
      ...(partial.interface ?? {}),
    },
  };
}

/** Fresh copy of `DEFAULT_SETTINGS` with its sub-objects deeply cloned. */
function freshDefaults(): Settings {
  return {
    schemaVersion: SETTINGS_SCHEMA_VERSION,
    gameplay: { ...DEFAULT_GAMEPLAY_SETTINGS },
    audio: { ...DEFAULT_AUDIO_SETTINGS },
    graphics: { ...DEFAULT_GRAPHICS_SETTINGS },
    controls: { ...DEFAULT_CONTROLS_SETTINGS },
    accessibility: { ...DEFAULT_ACCESSIBILITY_SETTINGS },
    interface: { ...DEFAULT_INTERFACE_SETTINGS },
  };
}

export function createSettingsStorage(
  opts: SettingsStorageOpts = {},
): SettingsStorage {
  const backend = opts.backend ?? defaultBackend();

  return {
    load(): Settings {
      const raw = backend.getItem(STORAGE_KEY);
      if (raw === null) return freshDefaults();
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        // Corrupted blob — fall back to defaults. Don't fail boot over a
        // bad settings file; the next save replaces the corruption.
        return freshDefaults();
      }
      if (typeof parsed !== 'object' || parsed === null) {
        // Not even an object — fall back to defaults.
        return freshDefaults();
      }
      const storedVersion = (parsed as { schemaVersion?: unknown }).schemaVersion;
      if (typeof storedVersion !== 'number') {
        // Missing or non-numeric schemaVersion — fall back.
        return freshDefaults();
      }
      if (storedVersion > SETTINGS_SCHEMA_VERSION) {
        // Stored blob is from a NEWER version of the app than we know
        // how to read. Don't risk surfacing a partial / mis-shapen
        // settings record; fall back to defaults rather than guessing.
        return freshDefaults();
      }
      // v4M1.4 — for older but additive schemas, merge with defaults
      // so existing user preferences carry forward and any new fields
      // pick up their default values. The schemaVersion in the
      // returned record is normalised to the current version by
      // `mergeWithDefaults` so the next save() persists the upgraded
      // shape. Only strictly-additive bumps are safe here; if a future
      // bump changes the shape of an existing field, treat it as a
      // hard break and short-circuit to `freshDefaults()` above.
      return mergeWithDefaults(parsed as Partial<Settings>);
    },
    save(settings: Settings): void {
      backend.setItem(STORAGE_KEY, JSON.stringify(settings));
    },
  };
}
